/**
 * LandingPagePremium — HR Engine — SaaS premium landing
 * Style: Deel / Rippling / Linear / Stripe / Vercel
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import TurnstileWidget from '../components/TurnstileWidget';
import {
  Sparkles, ArrowRight, ArrowUpRight, Check, ChevronDown, Globe, Menu, X,
  Users, Network, Target, Brain, BarChart3, Workflow, UserPlus,
  Database, Eye, Clock, Compass, Layers, Shield, Building2, Zap,
  TrendingDown, TrendingUp, FileCheck2, Bell, MessageSquare,
  Server, Lock, Activity, Mail, Phone, MapPin, ArrowDownRight,
} from 'lucide-react';

// ──────────────────────────────────────────────────────────────
// Smooth scroll helper
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ──────────────────────────────────────────────────────────────
// HEADER
function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    h(); window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const links = [
    { label: 'Plateforme', id: 'modules' },
    { label: 'Fonctionnalités', id: 'analytics' },
    { label: 'Solutions', id: 'enterprise' },
    { label: 'Ressources', id: 'cta-final' },
    { label: 'Devis', id: 'pricing' },
  ];

  return (
    <header data-testid="lp-header"
      className={`sticky top-0 z-50 transition-all ${
        scrolled ? 'bg-white/85 backdrop-blur-xl border-b border-ink-200/80 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.06)]'
                 : 'bg-white/60 backdrop-blur-md border-b border-transparent'
      }`}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-brand to-brand-hover shadow-[0_8px_24px_-10px_rgba(37,99,235,0.6)]">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="leading-none">
            <div className="text-base font-semibold tracking-tight text-ink-900">HR Engine</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map(l => (
            <button key={l.id} onClick={() => scrollTo(l.id)}
              data-testid={`nav-${l.id}`}
              className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100">
              {l.label}
              <ChevronDown className="h-3.5 w-3.5 text-ink-400" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button className="hidden sm:flex items-center gap-1 rounded-lg px-2 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100">
            <Globe className="h-4 w-4" /> FR
          </button>
          <Link to="/login" data-testid="header-login"
            className="hidden sm:inline-flex items-center rounded-lg border border-ink-200 bg-white px-3.5 py-2 text-sm font-medium text-ink-900 hover:border-brand/40 hover:bg-ink-50 transition">
            Se connecter
          </Link>
          <button onClick={() => scrollTo('pricing')} data-testid="header-cta"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-[0_8px_20px_-8px_rgba(30,58,138,0.5)] hover:bg-brand-hover transition">
            Demander un devis
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setOpen(o => !o)} className="lg:hidden rounded-lg p-2 text-ink-700 hover:bg-ink-100">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-ink-200 bg-white">
          <div className="px-4 py-2 space-y-1">
            {links.map(l => (
              <button key={l.id} onClick={() => { scrollTo(l.id); setOpen(false); }}
                className="w-full text-left rounded-lg px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100">
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

// ──────────────────────────────────────────────────────────────
// HERO DASHBOARD MOCKUP (the actual demo of the product)
function HeroDashboardMockup() {
  return (
    <div className="relative" data-testid="hero-mockup">
      {/* glow */}
      <div className="absolute -inset-8 -z-10 rounded-[40px] bg-gradient-to-tr from-brand/10 via-ai/10 to-transparent blur-3xl" />
      <div className="relative overflow-hidden rounded-3xl border border-ink-200/80 bg-white shadow-[0_40px_80px_-30px_rgba(15,23,42,0.25)]">
        <div className="flex">
          {/* Sidebar mockup */}
          <div className="hidden md:flex w-44 flex-col bg-[#0B1437] py-4 text-[11px] text-slate-300">
            <div className="flex items-center gap-2 px-4 pb-4 border-b border-white/10">
              <Sparkles className="h-4 w-4 text-blue-400" />
              <span className="font-semibold text-white">HR ENGINE</span>
            </div>
            <div className="mt-3 space-y-0.5 px-2">
              {['Dashboard', 'Organisation', 'Talents', 'Performance', 'Analyse', 'Données', 'Automatisations', 'Paramètres'].map((l, i) => (
                <div key={l} className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${i === 0 ? 'bg-white/10 text-white' : ''}`}>
                  <span className="h-1 w-1 rounded-full bg-current" />
                  {l}
                </div>
              ))}
            </div>
            <div className="mt-auto flex items-center gap-2 px-3 pt-4 border-t border-white/10">
              <div className="grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br from-slate-600 to-slate-900 text-[10px] font-bold text-white">FS</div>
              <div>
                <div className="text-[10px] font-semibold text-white">Fodé S.</div>
                <div className="text-[9px] text-slate-400">Administrateur</div>
              </div>
            </div>
          </div>

          {/* Main mock content */}
          <div className="flex-1 p-4 sm:p-5">
            <div className="flex items-center justify-between text-[10px] text-ink-500 mb-3">
              <span className="rounded-md border border-ink-200 px-2 py-1 bg-white">Tableau de bord ▾</span>
              <span className="rounded-md border border-ink-200 px-2 py-1 bg-white">Mai 2026 ▾</span>
            </div>
            <div className="mb-3">
              <div className="text-base font-bold text-ink-900">Bonjour Fodé 👋</div>
              <div className="text-[10px] text-ink-500">Voici un aperçu de votre organisation aujourd'hui.</div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                { l: 'Employés', v: '713', d: '+12 ce mois', c: 'bg-blue-100 text-blue-700', stroke: '#2563EB' },
                { l: 'Départements', v: '14', d: '+1 ce mois', c: 'bg-emerald-100 text-emerald-700', stroke: '#059669' },
                { l: 'Sites', v: '8', d: '+0 ce mois', c: 'bg-amber-100 text-amber-700', stroke: '#D97706' },
                { l: 'Engagement', v: '78%', d: '+4 pts', c: 'bg-violet-100 text-violet-700', stroke: '#7C3AED' },
              ].map((k) => (
                <div key={k.l} className="rounded-xl border border-ink-200 bg-white p-2">
                  <div className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-md ${k.c}`}>
                    <div className="h-2 w-2 rounded-full bg-current" />
                  </div>
                  <div className="text-[10px] text-ink-500">{k.l}</div>
                  <div className="text-base font-bold text-ink-900 leading-none">{k.v}</div>
                  <div className="text-[9px] font-medium text-ok mt-0.5">{k.d}</div>
                  <svg viewBox="0 0 60 16" className="mt-1 w-full" preserveAspectRatio="none">
                    <path d="M0 12 Q 10 8 20 10 T 40 4 T 60 6" stroke={k.stroke} fill="none" strokeWidth="1.5" />
                  </svg>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-3 rounded-xl border border-ink-200 bg-white p-3">
                <div className="text-[10px] font-semibold text-ink-900 mb-1">Évolution des effectifs</div>
                <div className="text-base font-bold text-ink-900">713 <span className="text-[10px] font-medium text-ok">+12% vs Mai 2025</span></div>
                <svg viewBox="0 0 200 60" className="mt-2 w-full">
                  <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity="0.3" /><stop offset="100%" stopColor="#2563EB" stopOpacity="0" /></linearGradient></defs>
                  <path d="M0 50 Q 20 45 40 42 T 80 35 T 120 25 T 160 15 T 200 8" stroke="#2563EB" fill="none" strokeWidth="2" />
                  <path d="M0 50 Q 20 45 40 42 T 80 35 T 120 25 T 160 15 T 200 8 L 200 60 L 0 60 Z" fill="url(#g1)" />
                  <circle cx="200" cy="8" r="3" fill="#2563EB" stroke="white" strokeWidth="1.5" />
                </svg>
              </div>
              <div className="col-span-2 rounded-xl border border-ink-200 bg-white p-3">
                <div className="text-[10px] font-semibold text-ink-900 mb-2">Répartition par département</div>
                <div className="flex items-center justify-center">
                  <div className="relative h-20 w-20">
                    <svg viewBox="0 0 40 40" className="h-20 w-20 -rotate-90">
                      <circle cx="20" cy="20" r="14" fill="none" stroke="#E2E8F0" strokeWidth="6" />
                      {[
                        { color: '#2563EB', pct: 26, off: 0 },
                        { color: '#7C3AED', pct: 22, off: 26 },
                        { color: '#F59E0B', pct: 18, off: 48 },
                        { color: '#059669', pct: 14, off: 66 },
                        { color: '#DC2626', pct: 12, off: 80 },
                        { color: '#94A3B8', pct: 8, off: 92 },
                      ].map((s, i) => (
                        <circle key={i} cx="20" cy="20" r="14" fill="none" stroke={s.color} strokeWidth="6"
                          strokeDasharray={`${(s.pct / 100) * 88} 88`} strokeDashoffset={-((s.off / 100) * 88)} />
                      ))}
                    </svg>
                    <div className="absolute inset-0 grid place-items-center text-center">
                      <div>
                        <div className="text-xs font-bold text-ink-900">713</div>
                        <div className="text-[8px] uppercase tracking-wider text-ink-500">Employés</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating AI card */}
      <div className="absolute -right-3 top-1/4 hidden lg:block w-64 rounded-2xl border border-ink-200 bg-white p-4 shadow-[0_20px_60px_-20px_rgba(124,58,237,0.35)] animate-[float_4s_ease-in-out_infinite]">
        <div className="flex items-center gap-2 mb-2">
          <div className="grid h-8 w-8 place-items-center rounded-lg bg-ai shadow-[0_8px_20px_-8px_rgba(124,58,237,0.5)]">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="text-sm font-semibold text-ink-900">Assistant IA</div>
          <span className="rounded-md bg-ai px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white">BETA</span>
        </div>
        <div className="text-xs text-ink-600 space-y-2">
          <div className="flex items-start gap-2">
            <TrendingDown className="h-3 w-3 mt-0.5 text-red-500 shrink-0" />
            <span><b>3 collaborateurs</b> à risque de départ.</span>
          </div>
          <div className="flex items-start gap-2">
            <Activity className="h-3 w-3 mt-0.5 text-amber-500 shrink-0" />
            <span>Finance : performance en baisse de <b>12%</b>.</span>
          </div>
          <div className="flex items-start gap-2">
            <Clock className="h-3 w-3 mt-0.5 text-blue-500 shrink-0" />
            <span><b>12 évaluations</b> à valider.</span>
          </div>
        </div>
        <button className="mt-3 flex w-full items-center justify-between rounded-lg bg-gradient-to-r from-ai to-ai-hover px-3 py-2 text-xs font-semibold text-white">
          Poser une question à l'IA <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// HERO
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.08),transparent)]" />
      <div className="absolute inset-x-0 top-0 -z-10 h-[400px] bg-gradient-to-b from-brand/5 via-transparent to-transparent" />

      <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-12 lg:grid-cols-2 lg:gap-8 lg:px-8 lg:pt-16">
        <div className="flex flex-col justify-center">
          <span className="inline-flex w-fit items-center gap-2 rounded-full border border-brand/20 bg-brand-soft/60 px-3 py-1.5 text-xs font-medium text-brand">
            <Sparkles className="h-3.5 w-3.5 text-ai" />
            SIRH IA nouvelle génération pour entreprises africaines
          </span>

          <h1 className="mt-6 text-4xl font-semibold leading-[1.1] tracking-tight text-ink-900 sm:text-5xl lg:text-6xl">
            Pilotez vos talents, vos performances et vos opérations RH depuis une seule plateforme{' '}
            <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">intelligente</span>.
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-ink-600 sm:text-lg">
            Centralisez vos collaborateurs, automatisez vos processus RH, analysez les performances
            et prenez de meilleures décisions grâce à l'IA.
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button onClick={() => scrollTo('pricing')} data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-white shadow-[0_16px_40px_-12px_rgba(30,58,138,0.5)] hover:bg-brand-hover transition-all hover:-translate-y-0.5">
              Demander un devis
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
            </button>
            <Link to="/login" data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-ink-200 bg-white px-5 py-3 text-sm font-semibold text-ink-900 hover:border-brand/40 hover:bg-ink-50 transition">
              Voir la plateforme
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-ink-600">
            {[
              { icon: Zap, text: 'Déploiement rapide' },
              { icon: Shield, text: 'Sécurisé & conforme' },
              { icon: Activity, text: 'Support local 24/7' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <div className="grid h-7 w-7 place-items-center rounded-lg bg-brand-soft text-brand">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center">
          <HeroDashboardMockup />
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// TRUST (KPI metrics only, no logos)
function Trust() {
  const metrics = [
    { icon: Building2, value: '+10 000', label: 'collaborateurs gérés', color: 'bg-blue-100 text-blue-700' },
    { icon: Users, value: '+50', label: 'entreprises partenaires', color: 'bg-emerald-100 text-emerald-700' },
    { icon: Activity, value: '99,9%', label: 'disponibilité de la plateforme', color: 'bg-amber-100 text-amber-700' },
    { icon: Workflow, value: '-40%', label: 'de tâches RH manuelles', color: 'bg-violet-100 text-violet-700' },
  ];
  return (
    <section className="border-y border-ink-200 bg-white py-12 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid grid-cols-1 gap-4 rounded-2xl border border-ink-200 bg-ink-50/50 p-6 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label} className="flex items-center gap-3">
              <div className={`grid h-12 w-12 place-items-center rounded-xl ${m.color}`}>
                <m.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="text-2xl font-semibold tracking-tight text-ink-900">{m.value}</div>
                <div className="text-xs text-ink-600">{m.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// PAIN POINTS
function PainPoints() {
  const items = [
    { icon: Database, title: 'Données dispersées', text: 'Les informations RH sont éparpillées et difficiles à exploiter.' },
    { icon: Eye, title: 'Manque de visibilité', text: 'Des décisions prises sans données fiables et en temps réel.' },
    { icon: Network, title: 'Perte de talents', text: 'Les risques de départ ne sont détectés qu\'une fois trop tard.' },
    { icon: Clock, title: 'Processus lents', text: 'Onboarding, évaluations et validations prennent des semaines.' },
    { icon: Compass, title: 'Pas de vision stratégique', text: 'Les RH restent opérationnelles au lieu d\'être un levier de croissance.' },
  ];
  return (
    <section className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
          <div className="lg:col-span-1">
            <h2 className="text-3xl font-semibold leading-tight tracking-tight text-ink-900 sm:text-4xl">
              Les RH modernes méritent{' '}
              <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">mieux</span> que des outils dépassés.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-ink-600">
              Les processus manuels, les fichiers Excel et le manque de visibilité coûtent du temps, de l'argent
              et des talents à votre organisation.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-2 lg:grid-cols-3">
            {items.map((it) => (
              <div key={it.title}
                className="group rounded-2xl border border-ink-200 bg-white p-5 transition hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-[0_16px_40px_-16px_rgba(15,23,42,0.12)]">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-ink-50 text-ink-700 group-hover:bg-brand-soft group-hover:text-brand transition">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-sm font-semibold text-ink-900">{it.title}</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-ink-600">{it.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// MODULES
function Modules() {
  const mods = [
    { icon: Users, title: 'People Mapping™',
      text: 'Organigramme intelligent, structures multisites et cartographie des équipes.',
      bg: 'bg-blue-100 text-blue-700' },
    { icon: Target, title: 'Performance System™',
      text: 'Gérez objectifs, évaluations, campagnes et scorecards en toute simplicité.',
      bg: 'bg-emerald-100 text-emerald-700' },
    { icon: Brain, title: 'Skills Intelligence™',
      text: 'Cartographie des compétences, détection des gaps et recommandations IA.',
      bg: 'bg-amber-100 text-amber-700' },
    { icon: BarChart3, title: 'Analytics & Insights',
      text: 'Dashboards temps réel, KPIs avancés et rapports exécutifs personnalisés.',
      bg: 'bg-violet-100 text-violet-700' },
    { icon: Workflow, title: 'Automation™',
      text: 'Workflows intelligents pour onboarding, validations et notifications.',
      bg: 'bg-rose-100 text-rose-700' },
    { icon: UserPlus, title: 'Recrutement™',
      text: 'Pipeline candidat, sourcing intelligent et scoring assisté par IA.',
      bg: 'bg-cyan-100 text-cyan-700' },
  ];
  return (
    <section id="modules" className="py-20 lg:py-28 bg-ink-50">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-3 lg:gap-16">
          <div className="lg:col-span-1">
            <h2 className="text-3xl font-semibold leading-tight tracking-tight text-ink-900 sm:text-4xl">
              Une plateforme{' '}
              <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">complète</span>{' '}
              pour piloter toute votre organisation.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-ink-600">
              Tous les modules sont conçus pour fonctionner ensemble : un seul référentiel, une seule expérience,
              une intelligence partagée.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:col-span-2">
            {mods.map((m) => (
              <div key={m.title} data-testid={`module-${m.title.replace(/\s+/g, '-').toLowerCase()}`}
                className="group rounded-2xl border border-ink-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-[0_24px_48px_-20px_rgba(15,23,42,0.15)]">
                <div className={`grid h-12 w-12 place-items-center rounded-xl ${m.bg}`}>
                  <m.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-ink-900">{m.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{m.text}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand opacity-90 transition group-hover:opacity-100 group-hover:gap-1.5">
                  En savoir plus <ArrowRight className="h-3 w-3" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// AI ASSISTANT (dark section)
function AISection() {
  const features = [
    'Détection des risques de départ',
    'Recommandations de formation',
    'Analyse des performances',
    'Synthèses RH automatiques',
    'Génération de rapports exécutifs',
    'Aide à la décision stratégique',
    'Alertes et insights intelligents',
  ];
  return (
    <section className="relative isolate overflow-hidden bg-[#0B1437] py-20 lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(50%_50%_at_85%_15%,rgba(124,58,237,0.30),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(45%_45%_at_5%_90%,rgba(37,99,235,0.22),transparent)]" />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 lg:grid-cols-2 lg:px-8">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/[0.08] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-amber-300" />
            IA Intégrée
          </span>
          <h2 className="mt-6 text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">
            Un assistant IA qui comprend vos enjeux{' '}
            <span className="text-amber-300">RH</span>.
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-200">
            L'intelligence artificielle au service de vos équipes pour anticiper, recommander et accélérer
            vos décisions.
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {features.map(f => (
              <li key={f} className="flex items-center gap-2 text-sm font-medium text-white">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-400/20">
                  <Check className="h-3 w-3 text-emerald-300" />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        {/* AI Chat mockup */}
        <div className="relative">
          <div className="absolute -inset-4 -z-10 rounded-[36px] bg-gradient-to-tr from-ai/30 to-blue-500/20 blur-3xl" />
          <div className="rounded-2xl border border-white/10 bg-white p-5 shadow-[0_40px_80px_-20px_rgba(15,23,42,0.6)]">
            <div className="flex items-center justify-between border-b border-ink-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-ai shadow-[0_8px_20px_-8px_rgba(124,58,237,0.6)]">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-ink-900">Assistant IA</span>
                    <span className="rounded-md bg-ai px-1.5 py-0.5 text-[9px] font-bold tracking-wider text-white">BETA</span>
                  </div>
                  <div className="text-[10px] text-ink-500">En ligne</div>
                </div>
              </div>
              <X className="h-4 w-4 text-ink-400" />
            </div>
            <div className="space-y-3 py-4">
              <div className="text-sm text-ink-900">Bonjour Fodé,</div>
              <div className="text-sm text-ink-700">Que puis-je analyser pour vous aujourd'hui ?</div>
              <div className="rounded-xl bg-ink-50 p-3 text-xs text-ink-600">
                <span className="font-semibold text-ink-900">Suggestion :</span> "Génère le rapport mensuel
                de la direction Finance et identifie les collaborateurs à risque."
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-ink-200 bg-white px-3 py-2.5">
              <input placeholder="Posez-moi une question…"
                className="flex-1 bg-transparent text-sm placeholder:text-ink-400 focus:outline-none" />
              <button className="grid h-8 w-8 place-items-center rounded-lg bg-ai text-white">
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// ANALYTICS
function AnalyticsSection() {
  return (
    <section id="analytics" className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="text-3xl font-semibold leading-tight tracking-tight text-ink-900 sm:text-4xl">
              Une <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">data visualization</span>{' '}
              digne d'un cockpit exécutif.
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-ink-600">
              Dashboards temps réel, heatmaps de compétences, répartition par site, indicateurs de
              turnover et d'engagement — la donnée RH enfin lisible.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { l: 'Turnover', v: '8,2%', d: '-1,3 pt', up: true, stroke: '#059669' },
                { l: 'Absentéisme', v: '3,4%', d: '+0,6 pt', up: false, stroke: '#DC2626' },
                { l: 'Engagement', v: '78%', d: '+5 pts', up: true, stroke: '#2563EB' },
                { l: 'Productivité', v: '+11%', d: 'vs mois précédent', up: true, stroke: '#7C3AED' },
              ].map(k => (
                <div key={k.l} className="rounded-2xl border border-ink-200 bg-white p-4">
                  <div className="flex items-center justify-between text-[11px] text-ink-500">
                    <span className="font-medium">{k.l}</span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-semibold tracking-tight text-ink-900">{k.v}</span>
                  </div>
                  <div className={`text-[11px] font-medium ${k.up ? 'text-ok' : 'text-bad'}`}>
                    {k.up ? '↑' : '↓'} {k.d}
                  </div>
                  <svg viewBox="0 0 80 22" className="mt-1.5 w-full">
                    <path d="M0 16 Q 10 12 20 13 T 40 8 T 60 6 T 80 4" stroke={k.stroke} strokeWidth="1.5" fill="none" />
                  </svg>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-rows-2 gap-4">
            {/* Heatmap */}
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-ink-900">Heatmap des compétences</div>
                <div className="flex items-center gap-1 text-[10px] text-ink-500">
                  Faible
                  <span className="flex gap-0.5">
                    {['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8'].map(c => (
                      <span key={c} className="h-2 w-2" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                  Élevé
                </div>
              </div>
              {['Leadership', 'Communication', 'Gestion de projet', 'Analyse de données', 'Techniques métier'].map((skill, ri) => (
                <div key={skill} className="mt-1 flex items-center gap-2">
                  <span className="w-32 text-xs text-ink-600 truncate">{skill}</span>
                  <div className="flex flex-1 gap-1">
                    {Array.from({ length: 10 }).map((_, ci) => {
                      const intensity = Math.min(3, Math.floor(((ri + ci) % 6) / 2));
                      const colors = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8'];
                      return <span key={ci} className="h-4 flex-1 rounded-sm" style={{ backgroundColor: colors[intensity] }} />;
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Repartition par site */}
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <div className="mb-3 text-sm font-semibold text-ink-900">Répartition par site</div>
              <div className="space-y-2">
                {[
                  { label: 'Siège (Abidjan)', value: 420, color: '#059669' },
                  { label: 'Dakar', value: 120, color: '#10B981' },
                  { label: 'Lomé', value: 85, color: '#2563EB' },
                  { label: 'Paris', value: 40, color: '#0F172A' },
                  { label: 'Autres', value: 48, color: '#94A3B8' },
                ].map(s => (
                  <div key={s.label} className="flex items-center gap-2 text-xs">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="flex-1 text-ink-700">{s.label}</span>
                    <div className="h-1.5 w-24 rounded-full bg-ink-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${(s.value / 420) * 100}%`, backgroundColor: s.color }} />
                    </div>
                    <span className="w-10 text-right font-semibold text-ink-900">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// AUTOMATION
function Automation() {
  const items = [
    { icon: UserPlus, label: 'Onboarding automatique' },
    { icon: Bell, label: 'Notifications & rappels' },
    { icon: FileCheck2, label: 'Validations simplifiées' },
    { icon: Database, label: 'Gestion des documents' },
    { icon: Target, label: 'Campagnes RH ciblées' },
    { icon: Shield, label: 'Audit & traçabilité' },
  ];
  return (
    <section className="py-20 lg:py-28 bg-ink-50">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-ai/20 bg-ai-soft px-3 py-1.5 text-xs font-medium text-ai">
              <Sparkles className="h-3.5 w-3.5" /> WORKFLOWS AUTOMATISÉS
            </span>
            <h2 className="mt-6 text-3xl font-semibold leading-tight tracking-tight text-ink-900 sm:text-4xl">
              Automatisez vos processus et libérez votre{' '}
              <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">potentiel</span>.
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-ink-600">
              Des workflows intelligents pour fluidifier chaque étape du cycle de vie de vos collaborateurs.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {items.map((it) => (
              <div key={it.label} className="flex items-center gap-3 rounded-xl border border-ink-200 bg-white p-4 transition hover:border-brand/30 hover:shadow-sm">
                <div className="grid h-9 w-9 place-items-center rounded-lg bg-ink-50 text-ink-700">
                  <it.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium text-ink-900">{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// ENTERPRISE
function EnterpriseFeatures() {
  const groups = [
    { icon: Shield, title: 'Sécurité avancée', text: 'Chiffrement bout-en-bout, audit complet, conformité ISO et RGPD.' },
    { icon: Lock, title: 'Rôles & permissions', text: 'Granularité par module, par site, par rôle. Délégation RBAC complète.' },
    { icon: Building2, title: 'Multi-sites & filiales', text: 'Gérez plusieurs entités, devises et conventions collectives au sein d\'un seul espace.' },
    { icon: Layers, title: 'Intégrations natives', text: 'Sage, Odoo, SAP, Microsoft 365, SSO Azure AD, Google Workspace.' },
    { icon: Server, title: 'Haute disponibilité', text: '99,9% d\'uptime garanti, infrastructure cloud redondante et hébergement régional.' },
    { icon: Activity, title: 'Audit & traçabilité', text: 'Journal d\'audit complet, exports SOC2, traçabilité de chaque action.' },
  ];
  return (
    <section id="enterprise" className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            Prêt pour les exigences{' '}
            <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">enterprise</span>.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-ink-600">
            Conçu pour les groupes industriels, les banques et les multinationales africaines.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div key={g.title} className="rounded-2xl border border-ink-200 bg-white p-6 transition hover:-translate-y-0.5 hover:shadow-[0_16px_40px_-16px_rgba(15,23,42,0.1)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-soft text-brand">
                <g.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-ink-900">{g.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-600">{g.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// DEMO & QUOTE REQUEST (replaces Pricing)
// forcedMode (optional): 'demo' | 'quote' — when set, hides the mode selector
// and locks the form to that single mode. Used to:
//   - Landing publique : forcedMode="quote" (devis uniquement)
//   - Page /demande-demo (lien interne partagé) : forcedMode="demo"
export function DemoRequest({ forcedMode = null }) {
  const [mode, setMode] = useState(forcedMode || 'demo'); // 'demo' | 'quote'
  const isLocked = Boolean(forcedMode);
  const [headcount, setHeadcount] = useState('200-1000');
  const [form, setForm] = useState({ company: '', name: '', email: '', phone: '', role: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const HEADCOUNTS = [
    { id: '<50', label: 'Moins de 50', sub: 'PME / Startup' },
    { id: '50-200', label: '50 – 200', sub: 'Croissance' },
    { id: '200-1000', label: '200 – 1 000', sub: 'Mid-Market' },
    { id: '1000-5000', label: '1 000 – 5 000', sub: 'Grande entreprise' },
    { id: '5000+', label: '5 000+', sub: 'Multinationale' },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      alert('Veuillez renseigner les champs requis (entreprise, nom, email).');
      return;
    }
    if (!turnstileToken) {
      alert('Veuillez compléter la vérification anti-bot.');
      return;
    }
    setSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${apiUrl}/api/leads/landing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, headcount, ...form, source: 'landing', turnstileToken }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || 'Server error');
      }
      setSubmitted(true);
    } catch (err) {
      alert(err.message || "Une erreur est survenue. Merci de réessayer ou de nous contacter à contact@hrengine.app.");
      // Single-use token — reset after any submission attempt
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="pricing" className="py-20 lg:py-28 bg-ink-50">
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-soft px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand">
            <Sparkles className="h-3.5 w-3.5 text-ai" />
            {isLocked && forcedMode === 'quote' && 'Demande de devis personnalisé'}
            {isLocked && forcedMode === 'demo' && 'Demande de démonstration'}
            {!isLocked && 'Démo & Devis personnalisé'}
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-ink-900 sm:text-4xl">
            {isLocked && forcedMode === 'quote' && 'Recevez un devis sur-mesure.'}
            {isLocked && forcedMode === 'demo' && 'Réservez votre démonstration.'}
            {!isLocked && "Choisissez votre point d'entrée."}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-600">
            {isLocked && forcedMode === 'quote' && 'Tarification adaptée à vos effectifs, modules et intégrations. Notre équipe vous répond sous 48h.'}
            {isLocked && forcedMode === 'demo' && 'Démonstration personnalisée 30 minutes par un expert RH. Notre équipe vous contacte sous 24h.'}
            {!isLocked && 'Démonstration personnalisée 30 minutes ou devis sur mesure pour votre organisation. Notre équipe vous répond sous 24h.'}
          </p>
        </div>

        <div className={`mt-12 grid gap-6 ${isLocked ? 'lg:grid-cols-1 max-w-3xl mx-auto' : 'lg:grid-cols-5'}`}>
          {/* Mode selector — hidden when forcedMode is set */}
          {!isLocked && (
          <div className="lg:col-span-2 space-y-4">
            <button onClick={() => setMode('demo')} data-testid="mode-demo"
              className={`group w-full text-left rounded-2xl border-2 p-6 transition-all ${
                mode === 'demo'
                  ? 'border-brand bg-white shadow-[0_24px_60px_-20px_rgba(30,58,138,0.25)]'
                  : 'border-ink-200 bg-white hover:border-brand/30'
              }`}>
              <div className="flex items-start gap-3">
                <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${
                  mode === 'demo' ? 'bg-brand text-white' : 'bg-brand-soft text-brand'
                }`}>
                  <Eye className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-ink-900">Demander une démo</h3>
                    {mode === 'demo' && <Check className="h-4 w-4 text-brand" />}
                  </div>
                  <p className="mt-1 text-sm text-ink-600">
                    Visite guidée de la plateforme par un expert RH. Cas d'usage adaptés à votre métier.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                    <span className="rounded-md bg-ink-100 px-2 py-0.5 font-medium text-ink-700">30 min</span>
                    <span className="rounded-md bg-ink-100 px-2 py-0.5 font-medium text-ink-700">Sans engagement</span>
                    <span className="rounded-md bg-ok-soft px-2 py-0.5 font-medium text-ok">Réponse 24h</span>
                  </div>
                </div>
              </div>
            </button>

            <button onClick={() => setMode('quote')} data-testid="mode-quote"
              className={`group w-full text-left rounded-2xl border-2 p-6 transition-all ${
                mode === 'quote'
                  ? 'border-ai bg-white shadow-[0_24px_60px_-20px_rgba(124,58,237,0.25)]'
                  : 'border-ink-200 bg-white hover:border-ai/30'
              }`}>
              <div className="flex items-start gap-3">
                <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${
                  mode === 'quote' ? 'bg-ai text-white' : 'bg-ai-soft text-ai'
                }`}>
                  <FileCheck2 className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-ink-900">Demander un devis</h3>
                    {mode === 'quote' && <Check className="h-4 w-4 text-ai" />}
                  </div>
                  <p className="mt-1 text-sm text-ink-600">
                    Tarification personnalisée selon vos effectifs, modules et intégrations souhaités.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                    <span className="rounded-md bg-ink-100 px-2 py-0.5 font-medium text-ink-700">Sur-mesure</span>
                    <span className="rounded-md bg-ink-100 px-2 py-0.5 font-medium text-ink-700">Multi-sites</span>
                    <span className="rounded-md bg-ai-soft px-2 py-0.5 font-medium text-ai">Devis sous 48h</span>
                  </div>
                </div>
              </div>
            </button>

            <div className="rounded-2xl border border-ink-200 bg-white p-5 text-xs text-ink-600">
              <div className="font-semibold text-ink-900 mb-2">Inclus dans toutes les offres</div>
              <ul className="space-y-1.5">
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ok" /> Onboarding et formation des équipes</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ok" /> Support local 24/7</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ok" /> Migration des données existantes</li>
                <li className="flex items-center gap-2"><Check className="h-3 w-3 text-ok" /> SLA 99,9% disponibilité</li>
              </ul>
            </div>
          </div>
          )}

          {/* Form */}
          <div className={isLocked ? 'lg:col-span-1' : 'lg:col-span-3'}>
            <form onSubmit={submit} data-testid="lead-form"
              className="rounded-2xl border border-ink-200 bg-white p-6 lg:p-8 shadow-[0_16px_40px_-16px_rgba(15,23,42,0.1)]">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ok-soft text-ok">
                    <Check className="h-7 w-7" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-ink-900">Demande bien reçue !</h3>
                  <p className="mt-2 text-sm text-ink-600 max-w-md mx-auto">
                    Notre équipe vous contactera sous 24h à l'adresse <b>{form.email}</b> pour planifier
                    votre {mode === 'demo' ? 'démonstration' : 'devis personnalisé'}.
                  </p>
                  <button type="button" onClick={() => { setSubmitted(false); setForm({ company:'', name:'', email:'', phone:'', role:'', message:'' }); }}
                    className="mt-6 text-sm font-medium text-brand hover:text-brand-hover">
                    Soumettre une autre demande
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-ink-900">
                    {mode === 'demo' ? 'Programmer ma démo' : 'Recevoir mon devis'}
                  </h3>
                  <p className="mt-1 text-sm text-ink-600">
                    Renseignez ces informations — un conseiller vous contacte rapidement.
                  </p>

                  {/* Headcount selector */}
                  <div className="mt-6">
                    <label className="text-xs font-semibold uppercase tracking-wider text-ink-700">
                      Nombre de collaborateurs *
                    </label>
                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-5">
                      {HEADCOUNTS.map(h => (
                        <button type="button" key={h.id} onClick={() => setHeadcount(h.id)}
                          data-testid={`hc-${h.id}`}
                          className={`rounded-xl border px-2 py-2.5 text-center transition ${
                            headcount === h.id
                              ? 'border-brand bg-brand-soft text-brand shadow-sm'
                              : 'border-ink-200 bg-white text-ink-700 hover:border-brand/40 hover:bg-ink-50'
                          }`}>
                          <div className="text-xs font-bold">{h.label}</div>
                          <div className="mt-0.5 text-[10px] opacity-80">{h.sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-xs font-semibold text-ink-700">Entreprise *</label>
                      <input value={form.company} onChange={e => setForm({...form, company: e.target.value})}
                        required data-testid="form-company"
                        className="mt-1 w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-ink-700">Votre fonction</label>
                      <input value={form.role} onChange={e => setForm({...form, role: e.target.value})}
                        placeholder="DRH, DSI, DG…" data-testid="form-role"
                        className="mt-1 w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-ink-700">Nom complet *</label>
                      <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                        required data-testid="form-name"
                        className="mt-1 w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-ink-700">Email professionnel *</label>
                      <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                        required data-testid="form-email"
                        className="mt-1 w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold text-ink-700">Téléphone (WhatsApp accepté)</label>
                      <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                        data-testid="form-phone"
                        className="mt-1 w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-xs font-semibold text-ink-700">
                        {mode === 'demo' ? 'Vos enjeux RH prioritaires' : 'Modules et intégrations souhaités'}
                      </label>
                      <textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                        rows={3} data-testid="form-message"
                        placeholder={mode === 'demo'
                          ? "Ex: piloter les performances, réduire le turnover, automatiser l'onboarding…"
                          : "Ex: People + Performance + Paie, intégration Sage, multi-pays Mali/Sénégal…"}
                        className="mt-1 w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                    </div>
                  </div>

                  <div className="mt-4">
                    <TurnstileWidget ref={turnstileRef} onToken={setTurnstileToken} />
                  </div>

                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] text-ink-500">
                      En soumettant, vous acceptez d'être contacté(e) par un conseiller HR Engine.
                    </p>
                    <button type="submit" disabled={submitting} data-testid="form-submit"
                      className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 ${
                        mode === 'demo'
                          ? 'bg-brand hover:bg-brand-hover shadow-[0_16px_40px_-12px_rgba(30,58,138,0.5)]'
                          : 'bg-ai hover:bg-ai-hover shadow-[0_16px_40px_-12px_rgba(124,58,237,0.5)]'
                      }`}>
                      {submitting ? 'Envoi…' : (mode === 'demo' ? 'Programmer ma démo' : 'Recevoir mon devis')}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// CTA FINAL
function CTAFinal() {
  return (
    <section id="cta-final" className="relative isolate overflow-hidden bg-[#0B1437] py-24 lg:py-32">
      <div className="absolute inset-0 bg-[radial-gradient(45%_45%_at_50%_-10%,rgba(124,58,237,0.30),transparent)]" />
      <div className="absolute inset-0 bg-[radial-gradient(40%_40%_at_30%_110%,rgba(37,99,235,0.24),transparent)]" />

      <div className="relative mx-auto max-w-4xl px-4 text-center lg:px-8">
        <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
          Transformez votre fonction RH en{' '}
          <span className="text-amber-300">levier stratégique</span>.
        </h2>
        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg">
          Rejoignez les organisations africaines qui ont déjà choisi HR Engine pour piloter leur capital humain.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button data-testid="final-cta" onClick={() => scrollTo('pricing')}
            className="group inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 text-sm font-semibold text-[#0B1437] shadow-[0_24px_60px_-16px_rgba(255,255,255,0.5)] hover:bg-slate-100 transition-all hover:-translate-y-0.5">
            Demander un devis personnalisé
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </button>
          <Link to="/login" className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/[0.06] px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/[0.12] transition">
            Accéder à mon compte
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-300">
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-400" /> Devis sous 48h</span>
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-400" /> Aucun engagement</span>
          <span className="flex items-center gap-1.5"><Check className="h-3 w-3 text-emerald-400" /> Support local</span>
        </div>
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// FOOTER
function Footer() {
  const cols = [
    { title: 'Plateforme', items: ['People Mapping', 'Performance', 'Skills Intelligence', 'Analytics', 'Automation', 'Paie'] },
    { title: 'Ressources', items: ['Documentation', 'Centre d\'aide', 'Blog', 'Changelog', 'Webinaires'] },
    { title: 'Société', items: ['À propos', 'Carrières', 'Presse', 'Contact', 'Partenaires'] },
    { title: 'Légal', items: ['Mentions légales', 'Conditions d\'utilisation', 'Politique de confidentialité', 'RGPD', 'Sécurité'] },
  ];
  return (
    <footer className="bg-[#0B1437] py-16 text-slate-300">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div className="leading-none">
                <div className="text-base font-semibold text-white">HR Engine</div>
                <div className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">by Webtech</div>
              </div>
            </Link>
            <p className="mt-4 max-w-xs text-sm text-slate-400">
              Le système d'exploitation RH intelligent conçu pour les entreprises africaines.
            </p>
            <div className="mt-5 space-y-2 text-xs text-slate-400">
              <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> contact@hrengine.app</div>
              <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> +223 76 00 00 00</div>
              <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Bamako · Abidjan · Dakar</div>
            </div>
          </div>
          {cols.map(c => (
            <div key={c.title} className="lg:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-white">{c.title}</div>
              <ul className="mt-4 space-y-2">
                {c.items.map(it => (
                  <li key={it}>
                    <a className="text-sm text-slate-400 hover:text-white transition" href="#">{it}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} HR Engine by Webtech. Tous droits réservés.</p>
          <p>ISO 27001 · RGPD · SOC2 Type II en cours</p>
        </div>
      </div>
    </footer>
  );
}

// ──────────────────────────────────────────────────────────────
// PAGE
export default function LandingPagePremium() {
  return (
    <div className="min-h-screen bg-ink-50 font-sans" data-testid="landing-premium">
      <style>{`
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
      `}</style>
      <Header />
      <Hero />
      <Trust />
      <PainPoints />
      <Modules />
      <AISection />
      <AnalyticsSection />
      <Automation />
      <EnterpriseFeatures />
      <DemoRequest forcedMode="quote" />
      <CTAFinal />
      <Footer />
    </div>
  );
}